import { KONE_ROOT_VARS } from './koneColors';

/**
 * Styles for the redesigned BusinessApplicationDetailsPage ("View 2").
 *
 * Ported from dummy_webpage/current/view2.css, EXCLUDING the app-shell/
 * sidebar/page-header chrome sections (1-2 in that file) — real Backstage
 * already renders that chrome around every page via <Page>/<Header>/
 * <Content>, so duplicating it here would fight the real theme instead of
 * complementing it. Only the content-area styles (view2.css sections 3-7:
 * cards/evaluation fields/posture dashboard/module grid/monitoring/AI
 * agent) are kept, verbatim in selectors/values, matching the original
 * STYLES constant's convention of embedding a <style> tag directly in the
 * page component.
 *
 * All colors below are KONE Flow Design System tokens from ./koneColors —
 * see that file's docstring for the full palette rationale. No purple, no
 * invented one-off shades; every color traces back to an approved token or
 * a plain rgba() tint of one.
 */
export const DETAILS_PAGE_STYLES = `
:root {
${KONE_ROOT_VARS}
}

.detail-container {
  display: grid;
  gap: 16px;
}

.card,
.primary_card {
  background: var(--kone-surface);
  border: 1px solid var(--kone-border);
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(20, 80, 245, 0.04);
}

.primary_card {
  background: linear-gradient(135deg, var(--kone-sand) 0%, var(--kone-surface) 100%);
}

.card h4,
.primary_card h4 {
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 600;
  color: var(--kone-text);
}

.card p,
.primary_card p,
.card span,
.primary_card span {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: var(--kone-text-muted);
}

.card a,
.primary_card a {
  color: var(--kone-blue);
}

.card a:hover,
.primary_card a:hover {
  text-decoration: underline;
}

.inline-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 12px;
  align-items: center;
}

.form-control {
  width: 100%;
  max-width: 320px;
  padding: 8px 10px;
  border: 1px solid var(--kone-border);
  border-radius: 8px;
  background: var(--kone-surface);
  color: var(--kone-text);
}

.action-button {
  border: none;
  border-radius: 999px;
  padding: 10px 16px;
  cursor: pointer;
  background-color: var(--kone-blue);
  color: var(--kone-white) !important;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  text-decoration: none;
}

.action-button:hover {
  background-color: var(--kone-blue);
  opacity: 0.92;
}

.action-button:disabled {
  opacity: 0.5;
  cursor: default;
}

.evaluation-heading {
  margin-top: 20px;
}

.security-summary-link {
  margin-bottom: 16px;
}

.security-summary-help {
  max-width: 720px;
  margin: 8px 0 0;
  color: var(--kone-text-muted);
  font-size: 13px;
  line-height: 1.5;
}

.security-summary-help code {
  color: var(--kone-text);
  font-family: inherit;
  font-weight: 600;
}

.request-evaluation-btn {
  margin-top: 12px;
  margin-bottom: 16px;
}

.evaluation-fields {
  margin: 16px 0;
  display: grid;
  gap: 4px;
}

.evaluation-field {
  display: flex;
  gap: 6px;
}

.evaluation-field dt {
  font-weight: 600;
  color: var(--kone-text);
  font-size: 14px;
}

.evaluation-field dd {
  margin: 0;
  font-size: 14px;
  color: var(--kone-text-muted);
}

.evaluation-pending {
  margin-top: 12px;
  font-size: 14px;
  color: var(--kone-text-muted);
  font-style: italic;
}

.summary-version-badge {
  display: inline-flex;
  align-items: center;
  margin-left: 10px;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  background: var(--kone-light-blue);
  color: var(--kone-blue);
}

.summary-version-badge--warning {
  background: rgba(255, 160, 35, 0.16);
  color: var(--kone-warning);
}

.not-applicable-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
  padding: 32px 16px;
  border: 1px dashed var(--kone-border);
  border-radius: 12px;
  background: var(--kone-surface-alt);
}

.not-applicable-icon {
  font-size: 28px;
}

.not-applicable-text {
  max-width: 360px;
  font-size: 14px;
  color: var(--kone-text-muted);
}

/* ---------- Overall Security Posture — standalone placeholder bar ----------
   Sits directly below the blue <Header>, full-width like the header but
   visually "thinner" with side gutters and rounded corners. Ported from
   dummy_webpage/current/view2.html/view2.css's posture-wrapper/
   posture-section/posture-toggle/posture-panel — this is a distinct,
   currently-empty accordion, NOT the same as the charts below (those live
   inside the Security Summary module view via SecuritySummaryAssessmentResults.tsx).
   Reserved for future functionality per product decision. */

.posture-wrapper {
  margin-bottom: 16px;
}

.posture-section {
  max-width: none;
  margin: 0 auto;
  background: var(--kone-surface);
  border: 1px solid var(--kone-border);
  border-radius: 14px;
  box-shadow: 0 4px 12px rgba(20, 80, 245, 0.04);
  overflow: hidden;
}

.posture-toggle {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border: none;
  margin: 0;
  padding: 14px 20px;
  background: var(--kone-blue);
  color: var(--kone-white);
  font: inherit;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  text-align: left;
}

.posture-toggle-label {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.posture-toggle-icon {
  font-size: 15px;
  line-height: 1;
}

.posture-chevron {
  flex: 0 0 auto;
  color: var(--kone-white);
  transition: transform 0.25s ease;
}

.posture-toggle[aria-expanded="true"] .posture-chevron {
  transform: rotate(180deg);
}

.posture-panel {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.35s ease;
}

.posture-section.is-expanded .posture-panel {
  grid-template-rows: 1fr;
}

.posture-panel-inner {
  overflow: hidden;
  min-height: 0;
  padding: 0 20px;
  transition: padding 0.35s ease;
}

.posture-section.is-expanded .posture-panel-inner {
  padding: 20px 20px 24px;
}

/* ---------- Security Summary Assessment Results charts (Security Summary module) ---------- */

.assessment-results-scores {
  display: grid;
  grid-template-columns: repeat(2, minmax(220px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

@media (max-width: 640px) {
  .assessment-results-scores {
    grid-template-columns: 1fr;
  }
}

.assessment-results-score-card {
  background: var(--kone-surface-alt);
  border: 1px solid var(--kone-border);
  border-radius: 12px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
}

.assessment-results-score-title {
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--kone-text-muted);
  margin: 0;
}

.radial-chart {
  --ring-size: 140px;
  width: var(--ring-size);
  height: var(--ring-size);
  border-radius: 50%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.radial-chart::before {
  content: '';
  position: absolute;
  inset: 12px;
  border-radius: 50%;
  background: var(--kone-surface-alt);
}

.radial-chart-value {
  position: relative;
  z-index: 1;
  font-size: 30px;
  font-weight: 700;
  color: var(--kone-text);
  line-height: 1;
}

.radial-chart-value span {
  font-size: 15px;
  font-weight: 600;
  vertical-align: top;
}

.assessment-results-score-status {
  font-size: 14px;
  font-weight: 600;
  padding: 3px 12px;
  border-radius: 999px;
  display: inline-block;
  transition: background-color 0.3s ease, color 0.3s ease;
}

.assessment-results-score-status.status-good {
  color: var(--kone-success);
  background: rgba(30, 210, 115, 0.16);
}

.assessment-results-score-status.status-medium {
  color: var(--kone-warning);
  background: rgba(255, 160, 35, 0.18);
}

.assessment-results-score-status.status-poor {
  color: var(--kone-error);
  background: rgba(255, 95, 40, 0.16);
}

.assessment-results-score-caption {
  font-size: 12px;
  color: var(--kone-text-muted);
  margin: 0;
}

.gauge-chart {
  width: 170px;
  height: 170px;
  position: relative;
}

.gauge-svg {
  width: 100%;
  height: 100%;
  display: block;
}

.gauge-tick {
  stroke: rgba(20, 20, 20, 0.16);
  stroke-width: 2;
  stroke-linecap: round;
}

.gauge-major-dot {
  fill: rgba(20, 20, 20, 0.22);
}

.gauge-track-path {
  fill: none;
  stroke: var(--kone-border);
  stroke-width: 16;
  stroke-linecap: round;
}

.gauge-value-path {
  fill: none;
  stroke-width: 16;
  stroke-linecap: round;
  transition: stroke 0.3s ease;
}

.gauge-handle-glow {
  opacity: 0.25;
  transition: fill 0.3s ease;
}

.gauge-handle {
  stroke: var(--kone-surface-alt);
  stroke-width: 2;
  transition: fill 0.3s ease;
}

.gauge-center-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  pointer-events: none;
}

.gauge-center-value {
  font-size: 36px;
  font-weight: 700;
  color: var(--kone-text);
  line-height: 1;
}

.gauge-center-label {
  font-size: 14px;
  font-weight: 600;
  transition: color 0.3s ease;
}

.assessment-results-metrics {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 20px;
}

@media (max-width: 720px) {
  .assessment-results-metrics {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.assessment-results-metric-card {
  background: var(--kone-surface-alt);
  border: 1px solid var(--kone-border);
  border-radius: 12px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.assessment-results-metric-value {
  font-size: 26px;
  font-weight: 700;
  color: var(--kone-text);
  line-height: 1;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.assessment-results-metric-badge {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: 0 0 auto;
}

.assessment-results-metric-badge.badge-none {
  background: var(--kone-success);
}

.assessment-results-metric-badge.badge-some {
  background: var(--kone-warning);
}

.assessment-results-metric-badge.badge-many {
  background: var(--kone-error);
}

.assessment-results-metric-label {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: var(--kone-text-muted);
  text-transform: uppercase;
}

.posture-empty-state,
.assessment-results-empty-state {
  font-size: 13px;
  color: var(--kone-text-muted);
  margin: 0;
  font-style: italic;
}

/* ---------- Application Modules dashboard ---------- */

.modules-card h4 {
  margin-bottom: 16px;
}

.dashboard-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

@media (max-width: 720px) {
  .dashboard-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 480px) {
  .dashboard-grid {
    grid-template-columns: 1fr;
  }
}

.dashboard-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 96px;
  padding: 18px 16px;
  border: none;
  border-radius: 12px;
  background: var(--kone-blue);
  color: var(--kone-white);
  font: inherit;
  font-size: 15px;
  font-weight: 600;
  text-align: center;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease, filter 0.18s ease;
  box-shadow: 0 2px 6px rgba(20, 80, 245, 0.18);
}

.dashboard-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 18px rgba(20, 80, 245, 0.28);
  filter: brightness(1.06);
}

.dashboard-card:active {
  transform: translateY(0);
  filter: brightness(0.97);
}

.dashboard-card:focus-visible {
  outline: 3px solid rgba(20, 80, 245, 0.4);
  outline-offset: 3px;
}

.dashboard-card.dashboard-card--disabled {
  background: var(--kone-neutral-4);
  cursor: pointer;
}

.dashboard-grid .dashboard-card-icon,
.dashboard-grid .dashboard-card-label {
  /* Override .card span's color: var(--kone-text-muted) (dark, muted
     text) -- .card span is a descendant selector (specificity 0,2,0),
     and the dashboard grid's wrapper div carries class "card" (see
     ApplicationModulesDashboard.tsx's div className="card modules-card"),
     so it reaches every span nested inside it, including these two,
     regardless of how deep. That beats the plain .dashboard-card
     color: white rule on the parent button, so without this override
     the label text and icon (fill="currentColor") both rendered as
     dark, semi-transparent text on the blue card background instead of
     white. This selector is bumped to specificity 0,2,0 itself (two
     classes) to match and beat .card span by appearing later in the
     stylesheet. */
  color: var(--kone-white);
}

.dashboard-card-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.dashboard-card-icon svg {
  width: 22px;
  height: 22px;
}

.dashboard-card-label {
  line-height: 1.3;
}

.detail-view-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  margin-bottom: 16px;
}

.back-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: transparent;
  color: var(--kone-blue);
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  padding: 4px 0;
  cursor: pointer;
}

.back-button:hover {
  text-decoration: underline;
}

.detail-view-title {
  margin: 0;
}

.application-compliance-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
}

.application-compliance-request-exception-button {
  flex: 0 0 auto;
  white-space: nowrap;
}

.placeholder-view {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
  padding: 32px 16px;
  border: 1px dashed var(--kone-border);
  border-radius: 12px;
  background: var(--kone-surface-alt);
}

.placeholder-view-icon {
  font-size: 28px;
}

.placeholder-view-text {
  max-width: 360px;
  font-size: 14px;
  color: var(--kone-text-muted);
}

/* ---------- Monitoring detail view ---------- */

.monitoring-field {
  margin-top: 16px;
}

.monitoring-field:first-child {
  margin-top: 0;
}

.monitoring-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: var(--kone-text);
  margin-bottom: 6px;
}

.monitoring-question {
  font-size: 14px;
  color: var(--kone-text);
  margin: 0 0 10px;
}

.secondary-button {
  background-color: var(--kone-surface-alt);
  color: var(--kone-text) !important;
  border: 1px solid var(--kone-border);
}

.secondary-button:hover {
  background-color: var(--kone-border);
  opacity: 1;
}

.monitoring-notice {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 16px;
  padding: 12px 14px;
  border-radius: 10px;
  background: rgba(255, 160, 35, 0.12);
  border: 1px solid rgba(255, 160, 35, 0.35);
  font-size: 13px;
  color: var(--kone-text);
  line-height: 1.5;
}

.monitoring-notice-icon {
  flex: 0 0 auto;
  font-size: 15px;
}

.monitoring-resolved {
  margin-top: 12px;
  font-size: 14px;
  color: var(--kone-text-muted);
}

.monitoring-resolved-alert {
  color: var(--kone-error);
  font-weight: 600;
}

.monitoring-readonly-field {
  display: flex;
  gap: 6px;
  font-size: 14px;
  margin-top: 8px;
}

.monitoring-readonly-field dt {
  font-weight: 600;
  color: var(--kone-text);
}

.monitoring-readonly-field dd {
  margin: 0;
  color: var(--kone-text-muted);
}

.monitoring-saved-notice {
  margin-top: 12px;
  font-size: 13px;
  color: var(--kone-success);
  font-weight: 600;
}

.monitoring-error-notice {
  margin-top: 12px;
  font-size: 13px;
  color: var(--kone-error);
  font-weight: 600;
}

/* ---------- Cybersecurity AI Agent — floating chat widget ---------- */

.ai-agent-launcher {
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 60px;
  height: 60px;
  border: none;
  border-radius: 50%;
  background: var(--kone-blue);
  color: var(--kone-white);
  font-size: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(20, 80, 245, 0.35);
  z-index: 1000;
  transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease, visibility 0.2s ease;
}

.ai-agent-launcher:hover {
  transform: translateY(-2px) scale(1.04);
  box-shadow: 0 12px 26px rgba(20, 80, 245, 0.45);
}

.ai-agent-launcher.is-hidden {
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transform: translateY(8px) scale(0.9);
}

.ai-agent-panel {
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 380px;
  height: 560px;
  max-height: calc(100vh - 48px);
  background: var(--kone-surface);
  border-radius: 16px;
  box-shadow: 0 16px 48px rgba(20, 20, 20, 0.24);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 1000;
  transform-origin: bottom right;
  transform: scale(0.92) translateY(12px);
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: transform 0.25s ease, opacity 0.25s ease, visibility 0.25s ease, height 0.25s ease;
}

.ai-agent-panel.is-open {
  transform: scale(1) translateY(0);
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
}

.ai-agent-panel.is-minimized {
  height: 60px;
}

.ai-agent-panel.is-minimized .ai-agent-messages,
.ai-agent-panel.is-minimized .ai-agent-input-row {
  display: none;
}

.ai-agent-panel.is-minimized .ai-agent-header {
  cursor: pointer;
  border-radius: 16px;
}

@media (max-width: 480px) {
  .ai-agent-panel {
    width: calc(100vw - 24px);
    height: calc(100vh - 24px);
    right: 12px;
    bottom: 12px;
  }
  .ai-agent-launcher {
    right: 16px;
    bottom: 16px;
  }
}

.ai-agent-header {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  background: var(--kone-blue);
  color: var(--kone-white);
  border-radius: 16px 16px 0 0;
}

.ai-agent-header-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
}

.ai-agent-header-icon {
  font-size: 16px;
}

.ai-agent-header-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.ai-agent-icon-btn {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.14);
  color: var(--kone-white);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  transition: background-color 0.15s ease;
}

.ai-agent-icon-btn:hover {
  background: rgba(255, 255, 255, 0.26);
}

.ai-agent-messages {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: var(--kone-surface-alt);
}

.ai-agent-message {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  max-width: 88%;
}

.ai-agent-message--user {
  align-self: flex-end;
  flex-direction: row-reverse;
}

.ai-agent-message--assistant {
  align-self: flex-start;
}

.ai-agent-avatar {
  flex: 0 0 auto;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: var(--kone-blue);
  color: var(--kone-white);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
}

.ai-agent-bubble {
  padding: 10px 14px;
  border-radius: 16px;
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-line;
  word-break: break-word;
}

.ai-agent-message--user .ai-agent-bubble {
  background: var(--kone-blue);
  color: var(--kone-white);
  border-bottom-right-radius: 4px;
}

.ai-agent-message--assistant .ai-agent-bubble {
  background: var(--kone-surface);
  color: var(--kone-text);
  border: 1px solid var(--kone-border);
  border-bottom-left-radius: 4px;
}

.ai-agent-thinking-bubble {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 12px 14px;
}

.ai-agent-thinking-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--kone-text-muted);
  animation: ai-agent-thinking-bounce 1.1s ease-in-out infinite;
}

.ai-agent-thinking-dot:nth-child(2) {
  animation-delay: 0.15s;
}

.ai-agent-thinking-dot:nth-child(3) {
  animation-delay: 0.3s;
}

@keyframes ai-agent-thinking-bounce {
  0%, 80%, 100% {
    transform: translateY(0);
    opacity: 0.5;
  }
  40% {
    transform: translateY(-4px);
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .ai-agent-thinking-dot {
    animation: none;
    opacity: 0.8;
  }
}

.ai-agent-input-row {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  border-top: 1px solid var(--kone-border);
  background: var(--kone-surface);
}

.ai-agent-input {
  flex: 1 1 auto;
  border: 1px solid var(--kone-border);
  border-radius: 999px;
  padding: 10px 16px;
  font-size: 14px;
  color: var(--kone-text);
  background: var(--kone-surface-alt);
}

.ai-agent-input:disabled {
  opacity: 0.6;
}

.ai-agent-send-btn {
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: var(--kone-blue);
  color: var(--kone-white);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.15s ease, opacity 0.15s ease;
}

.ai-agent-send-btn:hover {
  opacity: 0.9;
}

.ai-agent-send-btn:disabled {
  opacity: 0.5;
  cursor: default;
}

/* ---------- Application-scoped Compliance module ---------- */

.application-compliance-summary {
  margin-bottom: 16px !important;
}

.application-compliance-search {
  display: grid;
  gap: 6px;
  margin: 0 0 16px;
}

.application-compliance-search-label {
  color: var(--kone-text);
  font-size: 14px;
  font-weight: 600;
}

.application-compliance-search-control {
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 720px;
}

.application-compliance-search-input {
  flex: 1 1 auto;
  min-width: 0;
  padding: 10px 12px;
  border: 1px solid var(--kone-border);
  border-radius: 8px;
  background: var(--kone-surface);
  color: var(--kone-text);
  font: inherit;
}

.application-compliance-search-input:focus-visible {
  outline: 3px solid rgba(20, 80, 245, 0.28);
  outline-offset: 1px;
  border-color: var(--kone-blue);
}

.application-compliance-search-clear {
  flex: 0 0 auto;
  padding: 9px 14px;
  border: 1px solid var(--kone-border);
  border-radius: 999px;
  background: var(--kone-surface);
  color: var(--kone-text);
  cursor: pointer;
  font: inherit;
}

.application-compliance-search-clear:hover {
  background: var(--kone-border);
}

.application-compliance-search-clear:focus-visible {
  outline: 3px solid rgba(20, 80, 245, 0.28);
  outline-offset: 2px;
}

.application-compliance-usecases,
.application-compliance-requirements {
  display: grid;
  gap: 10px;
}

.application-compliance-usecase,
.application-compliance-group,
.application-compliance-requirement {
  border: 1px solid var(--kone-border);
  border-radius: 10px;
  background: var(--kone-surface-alt);
  overflow: hidden;
}

.application-compliance-usecase-toggle,
.application-compliance-group-toggle,
.application-compliance-requirement-toggle {
  width: 100%;
  border: none;
  background: transparent;
  color: var(--kone-text);
  font: inherit;
  cursor: pointer;
  text-align: left;
}

.application-compliance-usecase-toggle,
.application-compliance-group-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  font-weight: 600;
  line-height: 1.3 !important;
}

.application-compliance-usecase-toggle > span:first-child,
.application-compliance-group-toggle > span:first-child {
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  color: var(--kone-text) !important;
  line-height: 1.3 !important;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.application-compliance-usecase-toggle:hover,
.application-compliance-group-toggle:hover,
.application-compliance-requirement-toggle:hover {
  background: rgba(20, 80, 245, 0.06);
}

.application-compliance-static-heading {
  cursor: default;
}

.application-compliance-usecase-body,
.application-compliance-group-body {
  display: grid;
  gap: 10px;
  padding: 0 14px 14px;
}

.application-compliance-usecase-body > p,
.application-compliance-group-body > p {
  margin: 0;
}

.application-compliance-requirement {
  background: var(--kone-surface);
}

.application-compliance-requirement-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.application-compliance-requirement-toggle {
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: auto;
  padding: 10px 12px;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3 !important;
}

.application-compliance-requirement-name {
  /* Override the broader .card span color and line-height rules so the
     requirement name remains readable and vertically centered in its row. */
  color: var(--kone-text) !important;
  line-height: 1.3 !important;
}

.application-compliance-chevron {
  flex: 0 0 auto;
  color: var(--kone-blue) !important;
  font-size: 18px !important;
  line-height: 1 !important;
}

.application-compliance-requirement-body {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.2s ease;
}

.application-compliance-requirement.is-expanded .application-compliance-requirement-body {
  grid-template-rows: 1fr;
}

.application-compliance-requirement-content {
  min-height: 0;
  overflow: hidden;
  padding: 0 12px;
  transition: padding 0.2s ease;
}

.application-compliance-requirement.is-expanded .application-compliance-requirement-content {
  padding: 0 12px 12px;
}

.application-compliance-evidence-form {
  display: grid;
  gap: 12px;
  margin-bottom: 4px;
}

.application-compliance-automation-panel {
  display: grid;
  gap: 10px;
  margin-bottom: 12px;
  padding: 12px;
  border: 1px solid var(--kone-light-blue);
  border-radius: 9px;
  background: var(--kone-surface-alt);
}

.application-compliance-automation-status,
.application-compliance-automation-message,
.application-compliance-readonly-evidence p {
  margin: 0 !important;
  color: var(--kone-text);
  font-size: 13px;
  line-height: 1.45;
}

/* Automated evidence can list one attribute per line. */
.application-compliance-readonly-evidence p {
  white-space: pre-line;
  overflow-wrap: anywhere;
}

.application-compliance-automation-message {
  color: var(--kone-text-muted);
}

.application-compliance-readonly-evidence {
  display: grid;
  gap: 4px;
}

.application-compliance-readonly-evidence h6 {
  margin: 0;
  color: var(--kone-text);
  font-size: 13px;
}

.application-compliance-evidence-form > .action-button {
  justify-content: center;
  text-align: center;
}

.application-compliance-evidence-fields {
  display: grid;
  gap: 12px;
}

.application-compliance-field {
  display: grid;
  gap: 6px;
  color: var(--kone-text);
  font-size: 13px;
  font-weight: 600;
}

.application-compliance-field > span {
  color: var(--kone-text) !important;
  font-size: 13px !important;
  line-height: 1.3 !important;
}

.application-compliance-field input,
.application-compliance-field select,
.application-compliance-field textarea {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid var(--kone-border);
  border-radius: 8px;
  padding: 8px 10px;
  background: var(--kone-surface);
  color: var(--kone-text);
  font: inherit;
  font-size: 14px;
  font-weight: 400;
}

.application-compliance-field textarea {
  min-height: 88px;
  resize: vertical;
}

.application-compliance-status-select {
  max-width: 320px;
}

.application-compliance-justification-field textarea {
  max-width: 640px;
}

.application-compliance-form-error {
  margin: 0 !important;
  color: var(--kone-error) !important;
  font-size: 13px !important;
  font-weight: 600;
  line-height: 1.4 !important;
}

.application-compliance-description {
  margin: 0 0 10px !important;
  white-space: pre-line;
}

.application-compliance-links {
  margin: 0 0 10px;
  padding-left: 20px;
}

.application-compliance-standards {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.application-compliance-standard-chip {
  display: inline-flex;
  padding: 3px 8px;
  border: 1px solid var(--kone-border);
  border-radius: 999px;
  background: var(--kone-light-blue);
  color: var(--kone-blue) !important;
  font-size: 12px !important;
  line-height: 1.2 !important;
}

.application-compliance-history-toggle {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  margin-top: 8px;
  padding: 11px 13px;
  border: 1px solid var(--kone-light-blue);
  border-radius: 9px;
  background: var(--kone-white);
  color: var(--kone-black);
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  text-align: left;
}

.application-compliance-history-toggle:hover {
  border-color: var(--kone-blue);
  background: var(--kone-light-blue);
}

.application-compliance-history-toggle:focus-visible {
  outline: 3px solid var(--kone-blue);
  outline-offset: 2px;
}

.application-compliance-history-toggle-label {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
}

.application-compliance-history-toggle-label > span {
  font-size: 15px;
  line-height: 1;
}

.application-compliance-history-toggle .compliance-history-count {
  margin-left: auto;
}

.application-compliance-history-toggle-chevron {
  flex: 0 0 auto;
  color: var(--kone-blue);
  font-size: 12px;
}

.application-compliance-history-panel[hidden] {
  display: none;
}

.application-compliance-history {
  margin-top: 8px;
  padding-top: 18px;
  border-top: 1px solid var(--kone-border);
}

.application-compliance-history-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.application-compliance-history-heading h6 {
  margin: 0 0 4px;
  color: var(--kone-text);
  font-size: 15px;
}

.application-compliance-history-heading p {
  margin: 0;
  color: var(--kone-text-muted);
  font-size: 13px;
}

.compliance-history-count {
  flex: 0 0 auto;
  padding: 4px 8px;
  border-radius: 999px;
  background: var(--kone-light-blue);
  color: var(--kone-blue) !important;
  font-size: 11px !important;
  font-weight: 700;
  line-height: 1.2 !important;
}

.compliance-history-list {
  display: grid;
  gap: 16px;
}

.compliance-history-entry {
  padding: 18px;
  border: 1px solid var(--kone-border);
  border-left: 4px solid var(--kone-blue);
  border-radius: 10px;
  background: var(--kone-surface);
  box-shadow: 0 3px 10px rgba(20, 80, 245, 0.04);
}

.compliance-history-entry-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
}

.compliance-history-entry-header > div:first-child {
  min-width: 0;
}

.compliance-history-entry-header time {
  flex: 0 0 auto;
  color: var(--kone-text-muted);
  font-size: 12px;
  line-height: 1.4;
  text-align: right;
  white-space: nowrap;
}

.compliance-history-change-badge,
.compliance-history-status {
  display: inline-flex;
  align-items: center;
  min-height: 22px;
  margin-right: 6px;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 11px !important;
  font-weight: 700;
  line-height: 1.2 !important;
}

.compliance-history-change-badge--baseline {
  background: rgba(124, 58, 237, 0.1);
  color: #6d28d9 !important;
}

.compliance-history-change-badge--submission {
  background: rgba(20, 80, 245, 0.1);
  color: var(--kone-blue) !important;
}

.compliance-history-status--implemented {
  background: rgba(30, 210, 115, 0.15);
  color: #08783e !important;
}

.compliance-history-status--in-progress {
  background: rgba(255, 160, 35, 0.16);
  color: #9a5700 !important;
}

.compliance-history-status--not-applicable {
  background: rgba(100, 116, 139, 0.14);
  color: #475569 !important;
}

.compliance-history-status--not-started {
  background: rgba(20, 20, 20, 0.08);
  color: var(--kone-text-muted) !important;
}

.compliance-history-metadata {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 18px;
  margin: 0;
  padding: 12px 0;
  border-top: 1px solid var(--kone-border);
  border-bottom: 1px solid var(--kone-border);
}

.compliance-history-metadata div {
  min-width: 0;
}

.compliance-history-metadata dt,
.compliance-history-evidence-block h6 {
  margin: 0 0 4px;
  color: var(--kone-text-muted) !important;
  font-size: 11px !important;
  font-weight: 700;
  letter-spacing: 0.04em;
  line-height: 1.3 !important;
  text-transform: uppercase;
}

.compliance-history-metadata dd {
  overflow-wrap: anywhere;
  margin: 0;
  color: var(--kone-text) !important;
  font-size: 13px !important;
}

.compliance-history-evidence-block {
  margin-top: 14px;
  padding: 12px 14px;
  border-radius: 8px;
  background: var(--kone-surface-alt);
}

.compliance-history-evidence-block h6 {
  margin-bottom: 6px;
}

.compliance-history-evidence-block p {
  margin: 0;
  color: var(--kone-text-muted) !important;
  font-size: 13px !important;
  line-height: 1.55 !important;
  white-space: pre-line;
}

.compliance-history-links {
  display: grid;
  gap: 5px;
  margin: 0;
  padding-left: 18px;
}

.compliance-history-links li,
.compliance-history-links a {
  overflow-wrap: anywhere;
  font-size: 13px !important;
  line-height: 1.45 !important;
}

.compliance-history-empty,
.compliance-history-empty-state {
  color: var(--kone-text-muted) !important;
  font-size: 13px !important;
  font-style: italic;
}

.compliance-history-empty-state {
  margin: 0 !important;
  padding: 24px 0;
  text-align: center;
}

@media (max-width: 640px) {
  .application-compliance-history-heading,
  .compliance-history-entry-header {
    flex-direction: column;
  }

  .compliance-history-entry-header time {
    text-align: left;
  }

  .compliance-history-metadata {
    grid-template-columns: 1fr;
  }
}

@media (prefers-reduced-motion: reduce) {
  .application-compliance-requirement-body,
  .application-compliance-requirement-content {
    transition: none;
  }
}

.application-compliance-overall-progress {
  margin: 0 0 16px;
}

.application-compliance-progress {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 230px;
  color: var(--kone-text-muted);
}

.application-compliance-progress--inline {
  flex: 0 0 auto;
  min-width: 0;
  justify-content: flex-end;
  margin-left: auto;
}

.application-compliance-progress--inline .application-compliance-progress-label {
  text-align: right;
}

.application-compliance-progress-bar {
  display: inline-block;
  flex: 0 0 72px;
  height: 6px;
  overflow: hidden;
  border-radius: 999px;
  background: var(--kone-border);
}

.application-compliance-progress--inline .application-compliance-progress-bar {
  flex-basis: 56px;
}

.application-compliance-progress-bar-fill {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: var(--kone-success);
  transition: width 0.2s ease;
}

.application-compliance-progress-label {
  font-size: 12px !important;
  font-weight: 600;
  color: var(--kone-text-muted) !important;
  line-height: 1.2 !important;
  white-space: nowrap;
}

.application-compliance-status-pill {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  min-height: 22px;
  padding: 3px 8px;
  border: 1px solid transparent;
  border-radius: 999px;
  font-size: 11px !important;
  font-weight: 600;
  line-height: 1.2 !important;
  text-align: center;
  white-space: nowrap;
}

.application-compliance-requirement-badges {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 6px;
}

.application-compliance-status-pill--automated {
  border-color: rgba(20, 80, 245, 0.3);
  background: rgba(20, 80, 245, 0.1);
  color: var(--kone-blue) !important;
}

.application-compliance-label-filter {
  flex: 0 0 auto;
  min-width: 160px;
}

.application-compliance-status-pill--not-started {
  border-color: var(--kone-border);
  background: var(--kone-surface-alt);
  color: var(--kone-text-muted) !important;
}

.application-compliance-status-pill--in-progress {
  border-color: rgba(255, 160, 35, 0.35);
  background: rgba(255, 160, 35, 0.14);
  color: var(--kone-warning) !important;
}

.application-compliance-status-pill--implemented {
  border-color: rgba(30, 210, 115, 0.35);
  background: rgba(30, 210, 115, 0.14);
  color: var(--kone-success) !important;
}

.application-compliance-status-pill--not-applicable {
  border-color: rgba(120, 120, 120, 0.35);
  background: rgba(120, 120, 120, 0.1);
  color: var(--kone-text-muted) !important;
  font-style: italic;
}

.application-compliance-detail {
  display: grid;
  gap: 12px;
}

.application-compliance-detail--empty {
  padding: 16px 0;
}

.application-compliance-breadcrumb {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  margin: 0;
  color: var(--kone-text-muted);
  font-size: 12px !important;
  line-height: 1.4 !important;
}

.application-compliance-breadcrumb span {
  color: var(--kone-text-muted) !important;
  font-size: 12px !important;
  line-height: 1.4 !important;
}

.application-compliance-detail-title {
  margin: 0;
  color: var(--kone-text);
  font-size: 18px;
  line-height: 1.35;
}

.application-compliance-detail-content {
  min-width: 0;
  /* Space between the description and the status box below it. */
  margin-bottom: 16px;
}

/* "Last updated by/at" lines: smaller than the shared .evaluation-fields, and
   no outer margin (that space now sits above the status box instead). */
.application-compliance-audit-fields {
  margin: 0;
}

.application-compliance-audit-fields .evaluation-field dt,
.application-compliance-audit-fields .evaluation-field dd {
  font-size: 12px;
}

.application-compliance-detail-content .application-compliance-description {
  color: var(--kone-text);
}

@media (max-width: 640px) {
  .application-compliance-progress,
  .application-compliance-progress--inline {
    min-width: 0;
  }

  .application-compliance-progress--inline {
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  .application-compliance-progress-label {
    white-space: normal;
    text-align: right;
  }
}

@media (prefers-reduced-motion: reduce) {
  .application-compliance-progress-bar-fill {
    transition: none;
  }
}

.questionnaire-summary {
  margin-bottom: 16px !important;
  font-weight: 600;
  color: var(--kone-text) !important;
}

.questionnaire-status,
.questionnaire-error {
  margin: 16px 0 !important;
}

.questionnaire-error {
  color: var(--kone-danger) !important;
}

.questionnaire-fields {
  display: grid;
  gap: 10px;
  margin: 16px 0 0;
}

.questionnaire-field {
  display: grid;
  grid-template-columns: minmax(220px, 32%) minmax(0, 1fr);
  gap: 16px;
  padding: 10px 0;
  border-top: 1px solid var(--kone-border);
}

.questionnaire-field dt {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.5;
  color: var(--kone-text);
  overflow-wrap: anywhere;
}

.questionnaire-field dd {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: var(--kone-text-muted);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.questionnaire-technical-fields {
  margin-top: 24px;
  border-top: 1px solid var(--kone-border);
}

.questionnaire-technical-fields summary {
  padding: 14px 0 4px;
  cursor: pointer;
  color: var(--kone-text);
  font-size: 14px;
  font-weight: 600;
}

@media (max-width: 760px) {
  .questionnaire-field {
    grid-template-columns: 1fr;
    gap: 4px;
  }
}

/* Linked Requirements Note */
.application-compliance-linked-note {
  background: var(--kone-sand-light);
  border: 1px solid var(--kone-border);
  border-radius: 8px;
  padding: 12px 16px;
  margin: 12px 0;
  font-size: 14px;
  color: var(--kone-text-muted);
}

/* Application-to-application compliance link button (header) and status
 * notice — the button itself reuses .action-button's base styling. */
.application-compliance-link-button {
  white-space: normal;
  text-align: center;
}

.application-compliance-link-error-notice {
  color: var(--kone-error);
  font-size: 13px;
  font-weight: 600;
}

/* Read-only link status, shown instead of the link button to users who
   cannot edit Application Security. */
.application-compliance-link-readonly {
  font-size: 13px;
  font-weight: 600;
}

/* Application link picker modal — visually mirrors the Feature A
 * ("Link to another standard") modal in
 * compliance_management/src/components/CompliancePage.tsx, adapted to this
 * plugin's own KONE_ROOT_VARS token names (--kone-blue in place of
 * --kone-primary, --kone-surface-alt in place of --kone-surface-hover). */
.application-compliance-link-picker {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.application-compliance-link-picker-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  z-index: 0;
}

.application-compliance-link-picker-modal {
  position: relative;
  z-index: 1;
  background: var(--kone-surface);
  border: 1px solid var(--kone-border);
  border-radius: var(--kone-radius);
  width: 90%;
  max-width: 640px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

.application-compliance-link-picker-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid var(--kone-border);
}

.application-compliance-link-picker-header h3 {
  margin: 0;
  color: var(--kone-text);
}

.application-compliance-link-picker-close {
  background: none;
  border: none;
  font-size: 24px;
  color: var(--kone-text-muted);
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.application-compliance-link-picker-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.application-compliance-link-picker-step {
  display: grid;
  gap: 12px;
}

.application-compliance-link-picker-footer {
  padding: 16px 24px;
  border-top: 1px solid var(--kone-border);
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  align-items: center;
}

.application-link-target-list {
  max-height: 260px;
  overflow-y: auto;
  border: 1px solid var(--kone-border);
  border-radius: 8px;
}

.application-link-target-list-notice {
  margin: 0 !important;
  padding: 12px 16px !important;
  color: var(--kone-text-muted) !important;
  font-size: 13px !important;
  font-weight: 400 !important;
}

.application-link-target-option {
  padding: 12px 16px;
  border-bottom: 1px solid var(--kone-border);
  cursor: pointer;
  color: var(--kone-text);
  font-size: 14px;
  font-weight: 400;
  transition: background-color 0.2s;
}

.application-link-target-option:hover {
  background: var(--kone-surface-alt);
}

.application-link-target-option.selected {
  background: rgba(20, 80, 245, 0.08);
  color: var(--kone-blue);
}

.application-link-target-option:last-child {
  border-bottom: none;
}

.application-compliance-import-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.application-compliance-radio-option {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px;
  border: 1px solid var(--kone-border);
  border-radius: 8px;
  cursor: pointer;
  color: var(--kone-text);
  font-size: 14px;
  font-weight: 400;
  transition: border-color 0.2s;
}

.application-compliance-radio-option:hover {
  border-color: var(--kone-blue);
}

.application-compliance-radio-option input[type='radio'] {
  margin: 0;
  flex-shrink: 0;
}

/* --- Exception case: toggle, conversation bubbles, reply ----------------
 * Ported from dummy_webpage/current/requirement-exception.css (the
 * conversation/message/reply sections), mapped onto the class names
 * ComplianceView.renderExceptionCase emits. */
.application-compliance-exception-toggle {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  margin-top: 4px;
  padding: 11px 13px;
  border: 1px solid var(--kone-border);
  border-radius: 9px;
  background: var(--kone-surface-alt);
  color: var(--kone-text);
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  text-align: left;
}

.application-compliance-exception-toggle:hover {
  border-color: rgba(20, 80, 245, 0.35);
  background: rgba(20, 80, 245, 0.06);
}

.application-compliance-exception-toggle:focus-visible {
  outline: 3px solid rgba(20, 80, 245, 0.25);
  outline-offset: 2px;
}

.application-compliance-exception-toggle .compliance-exception-count {
  display: inline-flex;
  align-items: center;
  min-height: 22px;
  margin-left: auto;
  padding: 4px 8px;
  border-radius: 999px;
  background: var(--kone-light-blue);
  color: var(--kone-blue);
  font-size: 11px;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
}

.application-compliance-exception-toggle-chevron {
  flex: 0 0 auto;
  color: var(--kone-blue);
  font-size: 12px;
}

.application-compliance-exception-panel[hidden] {
  display: none;
}

.application-compliance-exception {
  display: grid;
  gap: 12px;
  margin-top: 8px;
  padding: 16px;
  border: 1px solid var(--kone-border);
  border-radius: 10px;
  background: var(--kone-surface);
}

.application-compliance-exception-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.application-compliance-exception-heading h6 {
  margin: 0;
  color: var(--kone-text);
  font-size: 15px;
  line-height: 1.35;
}

.application-compliance-exception-heading p {
  margin: 4px 0 0 !important;
  color: var(--kone-text-muted);
  font-size: 13px !important;
  line-height: 1.5 !important;
}

.application-compliance-exception .application-compliance-status-pill,
.application-compliance-exception-toggle .application-compliance-status-pill {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  min-height: 24px;
  padding: 4px 9px;
  border: 1px solid transparent;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
}

.application-compliance-status-pill--open {
  border-color: rgba(255, 160, 35, 0.35) !important;
  background: rgba(255, 160, 35, 0.14) !important;
  color: var(--kone-warning) !important;
}

.application-compliance-status-pill--rejected {
  border-color: rgba(255, 95, 40, 0.35) !important;
  background: rgba(255, 95, 40, 0.14) !important;
  color: var(--kone-error) !important;
}

.compliance-exception-conversation {
  display: grid;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid var(--kone-border);
}

.compliance-exception-messages {
  display: grid;
  gap: 12px;
  max-height: 420px;
  overflow-y: auto;
  padding: 4px 2px;
}

.compliance-exception-empty-state {
  margin: 0;
  color: var(--kone-text-muted);
  font-size: 13px;
}

.compliance-exception-message {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  max-width: 88%;
}

.compliance-exception-message--requester {
  justify-self: end;
  flex-direction: row-reverse;
}

.compliance-exception-message--admin {
  justify-self: start;
}

.compliance-exception-message-avatar {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: var(--kone-light-blue);
  color: var(--kone-blue);
  font-size: 10px;
  font-weight: 700;
}

.compliance-exception-message--requester .compliance-exception-message-avatar {
  background: var(--kone-blue);
  color: var(--kone-white);
}

.compliance-exception-message-bubble {
  min-width: 0;
  padding: 10px 12px;
  border: 1px solid var(--kone-border);
  border-radius: 12px;
  background: var(--kone-surface-alt);
}

.compliance-exception-message--requester .compliance-exception-message-bubble {
  border-color: var(--kone-blue);
  background: var(--kone-blue);
}

.compliance-exception-message-meta {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 5px;
}

.compliance-exception-message-meta strong {
  color: var(--kone-text);
  font-size: 12px;
}

.compliance-exception-message-meta time {
  color: var(--kone-text-muted);
  font-size: 11px;
  white-space: nowrap;
}

.compliance-exception-message-bubble p {
  margin: 0 !important;
  color: var(--kone-text);
  font-size: 13px !important;
  line-height: 1.5 !important;
  white-space: pre-line;
  overflow-wrap: anywhere;
}

.compliance-exception-message--requester .compliance-exception-message-meta strong,
.compliance-exception-message--requester .compliance-exception-message-meta time,
.compliance-exception-message--requester .compliance-exception-message-bubble p {
  color: var(--kone-white);
}

.compliance-exception-reply {
  display: grid;
  gap: 8px;
  padding-top: 4px;
}

.compliance-exception-reply label {
  color: var(--kone-text);
  font-size: 13px;
  font-weight: 600;
}

.compliance-exception-reply textarea {
  width: 100%;
  box-sizing: border-box;
  min-height: 78px;
  resize: vertical;
  border: 1px solid var(--kone-border);
  border-radius: 8px;
  padding: 9px 10px;
  background: var(--kone-surface);
  color: var(--kone-text);
  font: inherit;
  font-size: 13px;
}

.compliance-exception-reply textarea:focus-visible {
  outline: 3px solid rgba(20, 80, 245, 0.28);
  outline-offset: 1px;
  border-color: var(--kone-blue);
}

.compliance-exception-reply-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.compliance-exception-reply-actions > span {
  color: var(--kone-text-muted);
  font-size: 12px;
}

@media (max-width: 560px) {
  .application-compliance-exception-heading,
  .compliance-exception-reply-actions {
    flex-direction: column;
    align-items: flex-start;
  }

  .compliance-exception-message {
    max-width: 100%;
  }

  .compliance-exception-message-meta {
    align-items: flex-start;
    flex-direction: column;
    gap: 2px;
  }
}

/* ---------- Onsite Compliance Assessment ---------- */

.onsite-compliance-start,
.onsite-compliance-summary,
.onsite-compliance-history {
  margin-top: 20px;
}

.onsite-compliance-start {
  margin-top: 0;
}

.onsite-compliance-load-error h5 {
  margin: 0 0 10px;
  color: var(--kone-error);
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
}

.onsite-compliance-start > p {
  margin-bottom: 12px;
}

.onsite-compliance-empty-card {
  padding: 18px;
  border: 1px solid var(--kone-border);
  border-radius: 12px;
  background: var(--kone-surface-alt);
}

.onsite-compliance-empty-card p {
  color: var(--kone-text-muted);
  font-style: italic;
}

.compliance-score-circle {
  width: 88px;
  height: 88px;
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  border-radius: 50%;
}

.compliance-score-circle-inner {
  width: 68px;
  height: 68px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1px;
  border-radius: 50%;
  background: var(--kone-surface);
}

.card .compliance-score-value {
  color: var(--kone-text);
  font-size: 23px;
  font-weight: 700;
  line-height: 1;
}

.card .compliance-score-unit {
  align-self: center;
  color: var(--kone-text-muted);
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
}

.onsite-compliance-standard-groups {
  display: grid;
  gap: 12px;
}

.onsite-compliance-standard-group {
  overflow: hidden;
  border: 1px solid var(--kone-border);
  border-radius: 12px;
  background: var(--kone-surface-alt);
}

.onsite-compliance-standard-toggle {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px;
  border: none;
  background: transparent;
  color: var(--kone-text);
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.onsite-compliance-standard-toggle:hover {
  background: rgba(20, 80, 245, 0.06);
}

.onsite-compliance-standard-toggle > span:first-child {
  display: grid;
  gap: 3px;
}

.onsite-compliance-standard-toggle strong {
  color: var(--kone-text);
  font-size: 15px;
}

.onsite-compliance-standard-latest {
  color: var(--kone-text-muted) !important;
  font-size: 12px !important;
}

.onsite-compliance-standard-toggle-meta {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 10px;
  color: var(--kone-text-muted) !important;
  font-size: 12px !important;
}

.onsite-compliance-standard-assessments {
  display: grid;
  gap: 12px;
  padding: 12px 16px 16px;
  border-top: 1px solid var(--kone-border);
}

.onsite-compliance-history-entry {
  padding: 16px;
  border: 1px solid var(--kone-border);
  border-radius: 10px;
  background: var(--kone-surface);
}

.onsite-compliance-history-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}

.onsite-compliance-history-content strong {
  color: var(--kone-text);
}

.onsite-compliance-history-content .compliance-score-circle {
  width: 64px;
  height: 64px;
}

.onsite-compliance-history-content .compliance-score-circle-inner {
  width: 48px;
  height: 48px;
}

.onsite-compliance-history-content .compliance-score-value {
  font-size: 17px;
}

.onsite-compliance-load-error {
  padding: 18px;
  border: 1px solid rgba(255, 95, 40, 0.4);
  border-radius: 10px;
  background: rgba(255, 95, 40, 0.08);
}

.onsite-compliance-load-error-message {
  margin: 0 0 14px !important;
  color: var(--kone-text) !important;
  font-size: 14px !important;
  line-height: 1.5 !important;
}

@media (max-width: 640px) {
  .onsite-compliance-history-content {
    align-items: flex-start;
    flex-direction: column;
  }

  .onsite-compliance-standard-toggle {
    align-items: flex-start;
    flex-direction: column;
  }
}

.onsite-compliance-status {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 3px 10px;
  border: 1px solid transparent;
  border-radius: 999px;
  font-size: 12px !important;
  font-weight: 700;
  line-height: 1.2 !important;
}

.onsite-compliance-status--requested,
.onsite-compliance-status--in_progress {
  border-color: rgba(20, 80, 245, 0.3);
  background: var(--kone-light-blue);
  color: var(--kone-blue) !important;
}

.onsite-compliance-status--waiting_for_approval {
  border-color: rgba(255, 160, 35, 0.4);
  background: rgba(255, 160, 35, 0.18);
  color: var(--kone-warning) !important;
}

.onsite-compliance-status--completed {
  border-color: rgba(30, 210, 115, 0.4);
  background: rgba(30, 210, 115, 0.18);
  color: var(--kone-success) !important;
}

.onsite-compliance-status--failed {
  border-color: rgba(255, 95, 40, 0.4);
  background: rgba(255, 95, 40, 0.18);
  color: var(--kone-error) !important;
}

.onsite-compliance-history-title {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 4px;
}

.onsite-compliance-start h5,
.onsite-compliance-summary h5,
.onsite-compliance-history h5 {
  margin: 0 0 10px;
  color: var(--kone-text);
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
}

.onsite-compliance-start-fields {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.onsite-compliance-folder-selection {
  margin-top: 20px;
}

.onsite-compliance-folder-selection h6 {
  margin: 0 0 8px;
  color: var(--kone-text);
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
}

.onsite-compliance-folder-selection p {
  margin: 0 0 12px;
  color: var(--kone-text-muted);
}

.onsite-compliance-folder-selection .form-control {
  max-width: 420px;
}

.onsite-compliance-report-action {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
}

.card .onsite-compliance-download-message {
  color: var(--kone-error);
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;
}

.onsite-compliance-failure-title {
  margin: 12px 0 6px;
  color: var(--kone-error);
  font-size: 16px;
  font-weight: 700;
  line-height: 1.3;
}

.card .onsite-compliance-failure-message {
  margin: 0 0 12px;
  color: var(--kone-text);
  font-size: 14px;
  line-height: 1.5;
}

.back-button,
.card .back-button-arrow,
.card .back-button-text {
  color: var(--kone-blue);
}

.back-button:hover {
  text-decoration: none;
}

.card .back-button:hover .back-button-text {
  text-decoration: underline;
  text-underline-offset: 2px;
}

.card .back-button:hover .back-button-arrow {
  text-decoration: none;
}

.onsite-compliance-history-entry .onsite-compliance-failure-message {
  color: var(--kone-error);
  font-weight: 600;
  margin-top: 8px;
}

.onsite-compliance-status--reused {
  border-color: var(--kone-border);
  background: transparent;
  color: var(--konen-text);
}

.onsite-compliance-reuse-message {
  margin-top: 0.75rem;
  color: var(--kone-text-muted);
}

.onsite-compliance-active-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
`;
